import React from 'react';
import PropTypes from 'prop-types';
import { MenuItem, Tooltip } from '@mui/material';

import Select from '../../../../../../../components/v2/Select';
import Form from '../../../../../../Model/Form';
import { PPFSelection, getPostPaymentFormComparator } from '../../postPaymentFormSelection';
import texts from './texts.json';

/**
 * The single post payment form dropdown shared by AddProductModal and ProductView: the
 * coach's default or one of the coach's pinnable forms. Forms are sorted with the
 * default first, then alphabetically. If `value` is a form id that isn't in `forms` (a
 * pinned-then-archived form), it's rendered as a disabled "Unavailable form" entry so MUI
 * always has a matching MenuItem and the coach can see the pin is dead. That check only runs
 * once the forms list has actually loaded (`formsLoaded`) — both parents default their forms
 * state to `[]` while their fetch is in flight, and without a load signal a valid pin would
 * flash as "unavailable" during that window, while a load that resolves to a genuinely empty
 * list would never flag a dangling pin at all. A parent that doesn't pass `formsLoaded` falls
 * back to the old `forms.length > 0` heuristic, so this stays backward-safe.
 */
const PostPaymentFormSelect = ({
  forms,
  value,
  onChange,
  defaultFormId,
  disabled,
  size,
  formsLoaded,
}) => {
  const sortedForms = [...forms].sort(getPostPaymentFormComparator(defaultFormId));
  const loaded = formsLoaded ?? forms.length > 0;
  const isStalePin = loaded
    && value !== PPFSelection.COACH_DEFAULT
    && !sortedForms.some((form) => form.id === value);

  return (
    <Tooltip title={texts.tooltip} placement="right">
      <div>
        <Select
          size={size}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty
          disabled={disabled}
        >
          <MenuItem value={PPFSelection.COACH_DEFAULT}>{texts.coachDefaultPPF}</MenuItem>
          {sortedForms.map((form) => (
            <MenuItem key={form.id} value={form.id}>
              {form.id === defaultFormId ? `${form.formName} ${texts.defaultSuffix}` : form.formName}
            </MenuItem>
          ))}
          {isStalePin && (
            <MenuItem value={value} disabled>
              {texts.unavailableForm}
            </MenuItem>
          )}
        </Select>
      </div>
    </Tooltip>
  );
};

PostPaymentFormSelect.propTypes = {
  forms: PropTypes.arrayOf(PropTypes.instanceOf(Form)),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  defaultFormId: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  formsLoaded: PropTypes.bool,
};

PostPaymentFormSelect.defaultProps = {
  forms: [],
  value: PPFSelection.COACH_DEFAULT,
  defaultFormId: '',
  disabled: false,
  size: undefined,
  formsLoaded: undefined,
};

export default PostPaymentFormSelect;
